/* I testi e i disegni scritti dalla redazione per la pagina di un Paese
   (/paesi/[slug]), rifatta il 20/10/2026 sul modello uploads/turchia.html
   + uploads/oi-istruzioni-paese.md.

   **Che cosa sta qui e che cosa no.** La pagina è una sola per tutti i
   Paesi: titolo, numeri, temi, dispacci, report e casi vengono da monte
   (lib/paese-dati.ts) e valgono per qualunque Paese. Qui stanno solo le
   parti che le istruzioni segnano *(mio)*, cioè scritte a mano per un
   Paese preciso:
     - il sommario sotto il titolo;
     - titolo e testo del riquadro «Il Paese in sintesi»;
     - il disegno del crocevia (le direzioni si scelgono sul vicinato di
       ogni Paese) con le sue scritte;
     - il testo del riquadro «Lavori in …?» e i suoi cinque servizi;
     - la carta schematica dell'atlante e le frasi dei suoi nodi;
     - le preposizioni («sulla Turchia», «dalla Turchia»), che a monte non
       ci sono e che una regola sbaglierebbe («negli Stati Uniti»).

   **Popolati `turchia` e `serbia`** (quest'ultima bozza del 20/10/2026,
   da rileggere). Per gli altri Paesi la voce manca, e la
   pagina vive dei soli dati di monte. La regola, decisa qui una volta:
   un blocco che dipende da un testo redazionale non si rende, oppure ne
   resta solo la parte numerica. Mai un testo segnaposto in pagina. Quindi,
   senza voce:
     - niente sommario sotto il titolo;
     - «Il Paese in sintesi» si riduce alla riga dei numeri, senza titolo,
       testo né crocevia;
     - «Lavori in …?» resta, coi servizi sorteggiati a ogni richiesta
       (lib/sorteggio-servizi.ts) e senza il paragrafo;
     - l'atlante mostra i nodi di lib/corridoi.ts che nominano il Paese nel
       loro `short` (i Paesi attraversati), in elenco e senza carta; se non
       ce n'è nessuno, il blocco non c'è;
     - i titoli usano «Temi in Bosnia» quando «in» + nome regge, e la forma
       col punto mediano («Dispacci · Bosnia») dove servirebbe un articolo.

   Per aggiungere un Paese basta una voce in `PAESI_PAGINA` con lo slug
   della pagina: tutto quello che c'è si accende da sé.

   I nodi dell'atlante sono slug di lib/corridoi.ts, quindi nomi, tipi e
   pagine sono quelli dell'atlante vero. Il modello ne cita cinque: due
   (Oleodotto Iraq–Turchia, Elettrodotto Golfo–Europa) nell'atlante non
   c'erano e sono stati scritti il 20/10/2026 sui dispacci del modello
   (`oleodotto-iraq-turchia`, `elettrodotto-golfo-europa`, quest'ultimo
   col tipo nuovo `power`); «Bosforo e Dardanelli» sono due
   nodi distinti. In più passano dalla Turchia TANAP, BTC e Balkan Stream,
   che il modello non nomina e che qui ci sono. Sulla Serbia: Corridoio X
   (col ramo verso Budapest), Balkan Stream e Pireo; il Corridoio Verticale
   del gas, che i dispacci citano, nell'atlante non c'è.

   Senza direttiva e senza import dal server: lo leggono Server Component
   e il client della carta. */

/** Un Paese visto da una frase: le forme con la preposizione giusta. */
export type NomiPaese = {
  /** «in Turchia», per «Temi in Turchia», «Lavori in Turchia?». */
  in: string | null;
  /** «sulla Turchia», per «Dispacci sulla Turchia». */
  su: string | null;
  /** «dalla Turchia», per «nodi che passano dalla Turchia». */
  da: string | null;
};

/** Una direzione del crocevia: il quadratino col nome e la linea verso il
 *  centro, su cui corrono due quadratini rame. */
export type DirezioneCrocevia = {
  nome: string;
  /** Il quadratino (centro), nel viewBox 560 × 330. */
  x: number;
  y: number;
  /** La scritta: posizione e ancoraggio. */
  tx: number;
  ty: number;
  ancora: "start" | "middle" | "end";
  /** Il verso dei flussi: verso il centro o verso l'esterno. */
  verso: "dentro" | "fuori";
  /** Secondi di un giro, come i `dur` del modello. */
  durata: number;
};

export type Crocevia = {
  /** La scritta nel riquadro blu al centro. */
  centro: string;
  /** La riga rame in alto a sinistra del disegno. */
  occhiello: string;
  /** La didascalia: etichetta rame a sinistra, motto a destra. */
  titolo: string;
  motto: string;
  /** La descrizione per i lettori di schermo. */
  descrizione: string;
  direzioni: DirezioneCrocevia[];
};

/** Lo stile di una linea della carta, che è anche la sua voce di legenda.
 *  Ognuno ha un segno suo (classi `.oi-pa-at-<stile>` in globals.css):
 *  principale rame a trattini che scorrono, corridoio mare a trattini,
 *  oleodotto mare continuo, gasdotto a puntini tondi, trattativa a
 *  tratto-punto più tenue. Gasdotto e trattativa non si confondono più. */
export type StileLinea = "principale" | "corridoio" | "oleodotto" | "gasdotto" | "trattativa";

export const LEGENDA_LINEE: Record<StileLinea, string> = {
  principale: "Corridoio principale",
  corridoio: "Altri corridoi",
  oleodotto: "Oleodotto",
  gasdotto: "Gasdotto",
  trattativa: "In trattativa",
};

type Ancora = "start" | "middle" | "end";

/** Un nodo dell'atlante nella carta del Paese, nell'ordine dell'elenco. */
export type NodoCartaPaese = {
  /** Slug di lib/corridoi.ts. */
  slug: string;
  /** La frase dell'elenco. Senza, la prima frase del `hook` dell'atlante. */
  frase?: string;
  /** Il tipo nell'elenco, quando non è quello dell'atlante: «In trattativa»
   *  per un nodo che esiste solo come negoziato. Senza, `KIND_LABEL`. */
  tipo?: string;
  /** Il tracciato del nodo nel viewBox della carta, e il suo stile. Gli
   *  stretti non ne hanno: si accende il loro punto. */
  linea?: { d: string; stile: StileLinea };
};

/** Un punto della carta (Bosforo, Kars, Ceyhan…), coordinate letterali. */
export type PuntoCarta = {
  x: number;
  y: number;
  tx: number;
  ty: number;
  ancora: Ancora;
  testo: string;
  /** Gli slug dei nodi che lo accendono: il suo stretto, i tracciati che
   *  ci passano. */
  nodi: string[];
  /** Il nodo principale: rame pieno, con l'impulso. */
  principale?: boolean;
};

export type CartaPaese = {
  /** Il viewBox: 480 × 520 la Turchia (25/10/2026), 460 × 270 la Serbia.
   *  `x` / `y` per la carta del mondo della prima pagina, che tiene le
   *  coordinate del disegno del 15/10 (origine 160, 60). */
  viewBox: { x?: number; y?: number; w: number; h: number };
  /** «Bosforo» nella didascalia «● Bosforo · nodo principale». */
  principale: string;
  /** Il Paese, col nome: una sagoma (`sagoma`, un path) o il riquadro
   *  tenue arrotondato di prima (`riquadro`). Senza, nessun Paese: è la
   *  carta dei passaggi marittimi della prima pagina. */
  paese?: { testo: string; tx: number; ty: number; sagoma?: string; riquadro?: { x: number; y: number; w: number; h: number } };
  mari: { testo: string; x: number; y: number; ancora?: Ancora }[];
  /** Tracciati che non sono di nessun nodo dell'elenco (la rotta
   *  principale e quella del Capo nella carta del mondo): stesso segno dei
   *  tracciati, nessun collegamento. */
  rotte?: { d: string; stile: StileLinea }[];
  /** Etichette di legenda diverse da `LEGENDA_LINEE` («Rotta principale»
   *  invece di «Corridoio principale»). */
  legenda?: Partial<Record<StileLinea, string>>;
  /** Le direzioni ai bordi («← EUROPA», «RUSSIA ↑»). */
  bordi: { testo: string; x: number; y: number; ancora?: Ancora }[];
  punti: PuntoCarta[];
  nodi: NodoCartaPaese[];
  descrizione: string;
};

export type VocePaese = {
  nomi: NomiPaese;
  /** Il sommario sotto il titolo (Gelasio 21). */
  sommario: string;
  /** «Il Paese in sintesi»: titolo su due righe (la seconda nel mare) e testo. */
  sintesi: { riga1: string; riga2: string; testo: string };
  crocevia: Crocevia;
  /** «Lavori in …?»: il paragrafo e i cinque servizi (slug del catalogo). */
  lavori: { testo: string; servizi: string[] };
  carta: CartaPaese;
};

export const PAESI_PAGINA: Record<string, VocePaese> = {
  turchia: {
    nomi: { in: "in Turchia", su: "sulla Turchia", da: "dalla Turchia" },
    sommario:
      "Ankara fra Europa, Caucaso e Medio Oriente: energia, corridoi, finanza e politica estera, dispaccio dopo dispaccio.",
    sintesi: {
      riga1: "Un crocevia",
      riga2: "fra tre regioni.",
      testo:
        "Ponte energetico fra Golfo, Caspio ed Europa, corridoi commerciali verso l’Asia centrale, un vicinato che va da Cipro Nord all’Iraq.",
    },
    crocevia: {
      centro: "TURCHIA",
      occhiello: "ENERGIA · CORRIDOI · VICINATO",
      titolo: "Il crocevia",
      motto: "Sette direzioni, un solo Paese.",
      descrizione:
        "La Turchia al centro di sette direzioni: Europa, Mar Nero, Caucaso, Asia centrale, Iraq e Golfo, Levante, Mediterraneo; lungo le linee scorrono i flussi.",
      /* Coordinate, scritte e tempi del modello (`#trp0`…`#trp6`). */
      direzioni: [
        { nome: "EUROPA", x: 70, y: 70, tx: 80, ty: 58, ancora: "start", verso: "dentro", durata: 4 },
        { nome: "MAR NERO", x: 250, y: 40, tx: 250, ty: 26, ancora: "middle", verso: "fuori", durata: 5 },
        { nome: "CAUCASO", x: 470, y: 70, tx: 460, ty: 58, ancora: "end", verso: "dentro", durata: 6 },
        { nome: "ASIA CENTRALE", x: 520, y: 175, tx: 512, ty: 158, ancora: "end", verso: "fuori", durata: 4 },
        { nome: "IRAQ · GOLFO", x: 470, y: 285, tx: 456, ty: 289, ancora: "end", verso: "dentro", durata: 5 },
        { nome: "LEVANTE", x: 290, y: 300, tx: 302, ty: 304, ancora: "start", verso: "fuori", durata: 6 },
        { nome: "MEDITERRANEO", x: 80, y: 270, tx: 64, ty: 294, ancora: "start", verso: "dentro", durata: 4 },
      ],
    },
    lavori: {
      testo:
        "Prima di un accordo, di una fornitura o di un investimento: chi c’è dall’altra parte e quali vincoli contano.",
      servizi: ["presenza-internazionale", "due-diligence", "sanzioni", "import-export", "monitoraggio"],
    },
    /* La carta del 25/10/2026, copiata da uploads/atlante-turchia-riferimento.html
       (istruzioni uploads/ISTRUZIONI-AGENTE-atlante-paese.md): viewBox
       480 × 520, quasi quadrato come il riquadro, sagoma di Anatolia e
       Tracia, coordinate letterali del modello. Un tracciato per le voci
       03–09; gli stretti 01 e 02 non hanno linea e accendono il loro punto. */
    carta: {
      viewBox: { w: 480, h: 520 },
      principale: "Bosforo",
      descrizione:
        "Carta schematica dei passaggi che riguardano la Turchia: Bosforo e Dardanelli, Middle Corridor, Corridoio dello Sviluppo, oleodotto Iraq–Turchia, elettrodotto Golfo–Europa in trattativa, oleodotto BTC, gasdotti TANAP·TAP e Balkan Stream.",
      paese: {
        testo: "TURCHIA",
        tx: 236,
        ty: 232,
        sagoma:
          "M96 172 L140 166 L190 150 L262 140 L332 150 L382 158 L398 192 L388 240 L342 262 L292 270 L258 294 L226 286 L190 300 L150 292 L110 282 L86 252 L80 214 Z M94 166 L134 160 L130 140 L98 138 L80 150 Z",
      },
      mari: [
        { testo: "MAR NERO", x: 236, y: 100, ancora: "middle" },
        { testo: "MEDITERRANEO", x: 120, y: 372, ancora: "middle" },
        { testo: "CASPIO", x: 462, y: 290, ancora: "end" },
        { testo: "GOLFO", x: 462, y: 500, ancora: "end" },
      ],
      bordi: [
        { testo: "← EUROPA", x: 10, y: 138 },
        { testo: "ASIA CENTRALE →", x: 472, y: 148, ancora: "end" },
        { testo: "← PUGLIA", x: 10, y: 224 },
        { testo: "← BALCANI", x: 10, y: 100 },
        { testo: "RUSSIA ↑", x: 226, y: 56, ancora: "middle" },
        { testo: "GIORDANIA · SIRIA", x: 282, y: 388, ancora: "end" },
      ],
      punti: [
        { x: 138, y: 163, tx: 138, ty: 147, ancora: "middle", testo: "BOSFORO", principale: true, nodi: ["bosforo", "middle-corridor", "corridoio-sviluppo", "balkan-stream"] },
        { x: 96, y: 180, tx: 90, ty: 200, ancora: "end", testo: "DARDANELLI", nodi: ["dardanelli", "tanap"] },
        { x: 365, y: 176, tx: 365, ty: 165, ancora: "middle", testo: "KARS", nodi: ["middle-corridor"] },
        { x: 448, y: 192, tx: 456, ty: 208, ancora: "start", testo: "BAKU", nodi: ["middle-corridor", "btc", "tanap"] },
        { x: 255, y: 290, tx: 246, ty: 304, ancora: "end", testo: "CEYHAN", nodi: ["oleodotto-iraq-turchia", "elettrodotto-golfo-europa", "btc"] },
        { x: 370, y: 330, tx: 379, ty: 334, ancora: "start", testo: "KIRKUK", nodi: ["corridoio-sviluppo", "oleodotto-iraq-turchia"] },
        { x: 430, y: 455, tx: 439, ty: 459, ancora: "start", testo: "FAW", nodi: ["corridoio-sviluppo"] },
        { x: 300, y: 472, tx: 300, ty: 492, ancora: "middle", testo: "ARABIA SAUDITA", nodi: ["elettrodotto-golfo-europa"] },
      ],
      nodi: [
        { slug: "bosforo", frase: "Il Mar Nero passa da Istanbul." },
        { slug: "dardanelli" },
        {
          slug: "middle-corridor",
          frase: "Dall’Asia centrale all’Europa per il Caspio e la Turchia.",
          linea: { d: "M472 160 L448 192 Q408 176 365 176 Q262 150 138 163 Q82 156 8 148", stile: "principale" },
        },
        {
          slug: "corridoio-sviluppo",
          frase: "Dal porto di Faw all’Europa, attraverso Iraq e Turchia.",
          linea: { d: "M430 455 Q402 392 370 330 Q330 262 252 222 Q190 192 138 163", stile: "corridoio" },
        },
        { slug: "oleodotto-iraq-turchia", linea: { d: "M370 330 Q318 300 255 290", stile: "oleodotto" } },
        {
          slug: "elettrodotto-golfo-europa",
          tipo: "In trattativa",
          linea: { d: "M300 472 Q292 410 278 360 Q268 320 255 290", stile: "trattativa" },
        },
        { slug: "btc", linea: { d: "M448 192 Q402 222 342 242 Q292 262 255 290", stile: "oleodotto" } },
        { slug: "tanap", linea: { d: "M448 198 Q382 214 302 208 Q182 200 96 180 Q52 188 8 208", stile: "gasdotto" } },
        { slug: "balkan-stream", linea: { d: "M220 66 Q184 112 130 150 Q72 126 8 112", stile: "gasdotto" } },
      ],
    },
  },
  /* Bozza del 20/10/2026, da far rileggere alla redazione: i testi sono
     scritti sui dispacci della pagina (Corridoio Verticale del gas,
     Transnafta, rapporti con Zagabria, Mosca, la dorsale a 400 kV) e sui
     nodi dell'atlante. Il Corridoio Verticale del gas, che i dispacci
     citano, nell'atlante non c'è e non è stato inventato. */
  serbia: {
    nomi: { in: "in Serbia", su: "sulla Serbia", da: "dalla Serbia" },
    sommario:
      "Belgrado fra Unione europea, Russia e Cina: energia, corridoi, politica interna e vicinato balcanico, dispaccio dopo dispaccio.",
    sintesi: {
      riga1: "Senza mare,",
      riga2: "su tutte le rotte.",
      testo:
        "Candidata all’Unione europea e legata a Mosca per gas e petrolio, snodo del Corridoio X e del Balkan Stream, con il Kosovo aperto e rapporti tesi con Zagabria.",
    },
    crocevia: {
      centro: "SERBIA",
      occhiello: "ENERGIA · CORRIDOI · VICINATO",
      titolo: "Il crocevia",
      motto: "Sette direzioni, nessuno sbocco al mare.",
      descrizione:
        "La Serbia al centro di sette direzioni: Europa centrale, Danubio e Mar Nero, Bulgaria e Turchia, Egeo, Kosovo, Adriatico, Croazia e Bosnia; lungo le linee scorrono i flussi.",
      /* Le stesse posizioni della Turchia dove il vicinato lo consente; la
         direzione ovest (Croazia · Bosnia) è nuova, a metà altezza. */
      direzioni: [
        { nome: "EUROPA CENTRALE", x: 70, y: 70, tx: 80, ty: 58, ancora: "start", verso: "dentro", durata: 4 },
        { nome: "DANUBIO · MAR NERO", x: 470, y: 70, tx: 460, ty: 58, ancora: "end", verso: "fuori", durata: 5 },
        { nome: "BULGARIA · TURCHIA", x: 520, y: 175, tx: 512, ty: 158, ancora: "end", verso: "dentro", durata: 6 },
        { nome: "EGEO", x: 470, y: 285, tx: 456, ty: 289, ancora: "end", verso: "dentro", durata: 4 },
        { nome: "KOSOVO", x: 290, y: 300, tx: 302, ty: 304, ancora: "start", verso: "fuori", durata: 5 },
        { nome: "ADRIATICO", x: 80, y: 270, tx: 64, ty: 294, ancora: "start", verso: "fuori", durata: 6 },
        { nome: "CROAZIA · BOSNIA", x: 50, y: 170, tx: 40, ty: 154, ancora: "start", verso: "dentro", durata: 4 },
      ],
    },
    lavori: {
      testo:
        "Prima di un investimento, di una fornitura o di un acquisto: chi controlla la controparte, quali sanzioni pesano e da dove arrivano le materie prime.",
      servizi: ["presenza-internazionale", "due-diligence", "sanzioni", "materie-prime", "import-export"],
    },
    /* La carta di prima nel suo viewBox 460 × 270: dal 25/10/2026 sta nella
       nuova impaginazione, centrata sul millimetrato. I punti che erano
       «passaggi» senza nodo (Niš, Skopje, Tessalonica) si accendono con il
       Corridoio X, Tessalonica anche col Pireo. */
    carta: {
      viewBox: { w: 460, h: 270 },
      principale: "Belgrado",
      descrizione:
        "Carta schematica dei passaggi che riguardano la Serbia: il Corridoio X da Salisburgo a Tessalonica con Belgrado al centro e il ramo verso Budapest, il gasdotto Balkan Stream dalla Bulgaria all’Ungheria, il porto del Pireo.",
      paese: { testo: "SERBIA", tx: 192, ty: 140, riquadro: { x: 150, y: 60, w: 150, h: 150 } },
      mari: [
        { testo: "ADRIATICO", x: 30, y: 230 },
        { testo: "MAR NERO", x: 420, y: 120, ancora: "middle" },
        { testo: "EGEO", x: 345, y: 236, ancora: "middle" },
      ],
      bordi: [
        { testo: "← SALISBURGO", x: 14, y: 32 },
        { testo: "BUDAPEST ↑", x: 194, y: 12, ancora: "end" },
        { testo: "BULGARIA · TURCHIA →", x: 452, y: 194, ancora: "end" },
      ],
      punti: [
        { x: 215, y: 95, tx: 207, ty: 110, ancora: "end", testo: "BELGRADO", principale: true, nodi: ["corridoio-x"] },
        { x: 250, y: 160, tx: 258, ty: 170, ancora: "start", testo: "NIŠ", nodi: ["corridoio-x"] },
        { x: 255, y: 225, tx: 263, ty: 229, ancora: "start", testo: "SKOPJE", nodi: ["corridoio-x"] },
        { x: 300, y: 258, tx: 300, ty: 268, ancora: "middle", testo: "TESSALONICA", nodi: ["corridoio-x", "pireo"] },
        { x: 380, y: 250, tx: 388, ty: 254, ancora: "start", testo: "PIREO", nodi: ["pireo"] },
      ],
      /* Disegnati qui, sul passo della carta turca di prima. Il Corridoio X
         porta anche il ramo Xb verso Budapest, come secondo tratto della linea. */
      nodi: [
        {
          slug: "corridoio-x",
          frase: "Da Salisburgo a Tessalonica, con Belgrado al centro.",
          linea: {
            d: "M14 40 Q 110 62 215 95 Q 240 125 250 160 Q 256 195 255 225 Q 262 248 300 258 M200 4 Q 206 50 215 95",
            stile: "principale",
          },
        },
        {
          slug: "balkan-stream",
          frase: "Il gas russo entra dalla Bulgaria ed esce verso l’Ungheria.",
          linea: { d: "M452 178 Q 380 170 300 150 Q 262 120 252 70 Q 248 36 246 4", stile: "gasdotto" },
        },
        {
          slug: "pireo",
          frase: "I container cinesi sbarcano al Pireo e salgono in treno verso Belgrado.",
          linea: { d: "M380 250 Q 340 262 300 258", stile: "corridoio" },
        },
      ],
    },
  },
};

export function vocePaese(slug: string): VocePaese | null {
  return PAESI_PAGINA[slug] ?? null;
}

/** I nomi che non reggono un «in» secco: plurali e nomi con l'articolo
 *  («negli Stati Uniti», «nel Regno Unito», «nella Repubblica Ceca»). */
const CON_ARTICOLO = /^(Stati|Regno|Paesi|Emirati|Isole|Filippine|Maldive|Seychelles|Comore|Bahamas|Repubblica|Federazione|Città|Santa|Distretto|Sangiaccato)\b/;

/** «per la Turchia», per «Che decisione devi prendere per la Turchia?»
 *  dell'Accoglienza. Si ricava dalle forme che ci sono già, per non
 *  scrivere una quarta preposizione a mano su ogni voce: «sulla Turchia»
 *  diventa «per la Turchia». Solo da «su», che porta l'articolo: «in Iran»
 *  non lo porta, e «per Iran» è sbagliato (si dice «per l’Iran»). Senza la
 *  forma con «su» (i Paesi senza voce in `PAESI_PAGINA`) torna null e la
 *  domanda resta senza nome. */
export function perIlPaese(nomi: NomiPaese): string | null {
  const su = nomi.su?.match(/^(sull[ao]|sul|sugli|sulle|sui|sull’|sull')\s*(.+)$/);
  if (su) {
    const articolo: Record<string, string> = {
      sulla: "la ", sullo: "lo ", sul: "il ", sugli: "gli ", sulle: "le ", sui: "i ", "sull’": "l’", "sull'": "l’",
    };
    return `per ${articolo[su[1]!]}${su[2]}`;
  }
  return null;
}

/** Le forme del Paese: quelle della redazione, se ci sono, altrimenti
 *  «in» + nome dove regge e nient'altro (chi riceve `null` usa la forma
 *  col punto mediano). */
export function nomiPaese(nome: string, voce: VocePaese | null): NomiPaese {
  if (voce) return voce.nomi;
  return { in: CON_ARTICOLO.test(nome) ? null : `in ${nome}`, su: null, da: null };
}
